import LystLogo from "img/branding/lyst-logo-2020-black.svg?inline";
import React, { useState } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Button } from "web/react/emo/button/button";
import { HStack } from "web/react/emo/h-stack/h-stack";
import { BaseText } from "web/react/emo/shared/components/base-text";
import { Text } from "web/react/emo/text/text";
import { VStack } from "web/react/emo/v-stack/v-stack";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./partners-nav.css";
import { scrollToSection } from "./scroll-to-section";

interface NavLink {
    labelKey: string;
    analyticsLabel: string;
    targetId: string;
}

const NAV_LINKS: NavLink[] = [
    { labelKey: "partners.nav.the_model", analyticsLabel: "the_model", targetId: "the-model" },
    { labelKey: "partners.nav.benefits", analyticsLabel: "benefits", targetId: "benefits" },
    { labelKey: "partners.nav.audience", analyticsLabel: "audience", targetId: "audience" },
    {
        labelKey: "partners.nav.integration",
        analyticsLabel: "integration",
        targetId: "integration",
    },
    {
        labelKey: "partners.nav.trust_and_compliance",
        analyticsLabel: "trust_and_compliance",
        targetId: "trust-and-compliance",
    },
    {
        labelKey: "partners.nav.onboarding",
        analyticsLabel: "onboarding",
        targetId: "onboarding",
    },
];

export default function PartnersNav(): JSX.Element {
    const [menuOpen, setMenuOpen] = useState(false);

    function handleNavLinkClick(link: NavLink): void {
        analytics.event({
            category: "partners_nav",
            action: "clicked",
            label: link.analyticsLabel,
        });
        scrollToSection(link.targetId);
        setMenuOpen(false);
    }

    function handleSellClick(): void {
        analytics.event({
            category: "partners_nav",
            action: "clicked",
            label: "sell_on_lyst_shop",
        });
        scrollToSection("contact-form");
        setMenuOpen(false);
    }

    function handleNeedHelpClick(): void {
        analytics.event({ category: "partners_nav", action: "clicked", label: "need_help" });
    }

    function handleLogoClick(e: React.MouseEvent): void {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    return (
        <>
            <nav className={styles.nav}>
                <div className={styles.inner}>
                    <div className={styles.navLeft}>
                        <button
                            className={styles.logoButton}
                            aria-label={gettext("back_to_top")}
                            onClick={handleLogoClick}
                        >
                            <LystLogo className={styles.logo} />
                        </button>

                        <ul className={styles.linkList}>
                            {NAV_LINKS.map((link) => (
                                <li key={link.targetId}>
                                    <button
                                        className={styles.navLink}
                                        onClick={() => handleNavLinkClick(link)}
                                    >
                                        <Text textStyle="body-s" as="span">
                                            {gettext(link.labelKey)}
                                        </Text>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={styles.actions}>
                        <HStack spacing="sm" align="center">
                            <Button
                                variant="secondary"
                                title={gettext("checkout.need_help")}
                                href="https://lystpartnersupporthelp.zendesk.com/hc/en-us"
                                target="_blank"
                                onClick={handleNeedHelpClick}
                            />
                            <Button
                                variant="primary"
                                title={gettext("partners.nav.sell_on_lyst_shop")}
                                onClick={handleSellClick}
                            />
                        </HStack>
                    </div>

                    <button
                        className={styles.hamburger}
                        onClick={() => setMenuOpen((prev) => !prev)}
                        aria-label={
                            menuOpen
                                ? gettext("header.navigation.close_menu")
                                : gettext("header.navigation.open_menu")
                        }
                        aria-expanded={menuOpen}
                    >
                        <span
                            className={menuOpen ? styles.hamburgerBar1Open : styles.hamburgerBar}
                        />
                        <span
                            className={menuOpen ? styles.hamburgerBar2Open : styles.hamburgerBar}
                        />
                        <span
                            className={menuOpen ? styles.hamburgerBar3Open : styles.hamburgerBar}
                        />
                    </button>
                </div>
            </nav>

            {menuOpen && (
                <div className={styles.mobileMenu} role="dialog" aria-modal="true">
                    <ul className={styles.mobileLinkList}>
                        {NAV_LINKS.map((link) => (
                            <li key={link.targetId}>
                                <button
                                    className={styles.mobileLinkItem}
                                    onClick={() => handleNavLinkClick(link)}
                                >
                                    <BaseText textStyle="body" as="span">
                                        {gettext(link.labelKey)}
                                    </BaseText>
                                    <SVGIcon name="chevron" className={styles.mobileLinkItemIcon} />
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className={styles.mobileActionsWrapper}>
                        <div className={styles.mobileActionsInner}>
                            <VStack spacing="sm">
                                <Button
                                    variant="primary"
                                    width="full"
                                    title={gettext("partners.nav.sell_on_lyst_shop")}
                                    onClick={handleSellClick}
                                />
                                <Button
                                    variant="secondary"
                                    width="full"
                                    title={gettext("checkout.need_help")}
                                    href="https://lystpartnersupporthelp.zendesk.com/hc/en-us"
                                    target="_blank"
                                    onClick={handleNeedHelpClick}
                                />
                            </VStack>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
